import { getRelativeTime } from '@thoughtspot/date-format-util';
import { translate as t } from '@thoughtspot/i18n';
import { Vertical } from '@thoughtspot/radiant-react/widgets/layout';
import { Popover } from '@thoughtspot/radiant-react/widgets/popover';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import { getTSAdminUserGuid, getUserGuid, isPrincipalImportExportEnabled, isScriptabilityEnabled, } from '@thoughtspot/session-service';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { getTranslatedInfoForMessageCode } from "../../../../utils/error-handling/error-strings-mapping";
import { getErrorObjectFromGraphQLErrors } from "../../../../utils/error-handling/error-utils";
import { MoreOptions } from '../../../user-management/more-options/more-options';
import { deepOmit, getErrorCode } from '../../../user-management/util';
import styles from './components/users.module.scss';
import { getAuthTypeLabel, getStatusDisplayLabel, isLocalUser, ListActionTypeV2, TableColumnId, UserAccountStatus, V2TranslationKeys, } from './constants';
const getStatusSpecificActions = (user, dispatch) => {
    const { userState, type } = user;
    switch (userState) {
        case UserAccountStatus.ACTIVE:
            return [
                {
                    id: ListActionTypeV2.DEACTIVATE,
                    label: t('users.v2.okta.deactivateUser'),
                    onClick: dispatch(ListActionTypeV2.DEACTIVATE),
                },
            ];
        case UserAccountStatus.PENDING:
            if (isLocalUser(type)) {
                return [
                    {
                        id: ListActionTypeV2.RESEND_ACTIVATION_MAIL,
                        label: t('oktaUser.RESEND_ACTIVATION_MAIL'),
                        onClick: dispatch(ListActionTypeV2.RESEND_ACTIVATION_MAIL),
                    },
                ];
            }
            return [
                {
                    id: ListActionTypeV2.ACTIVATE,
                    label: t('users.v2.okta.activateUser'),
                    onClick: dispatch(ListActionTypeV2.ACTIVATE),
                },
            ];
        case UserAccountStatus.LOCKED:
            return [
                {
                    id: ListActionTypeV2.UNLOCK,
                    label: t('oktaUser.UNLOCK'),
                    onClick: dispatch(ListActionTypeV2.UNLOCK),
                },
            ];
        case UserAccountStatus.SUSPENDED:
            return [
                {
                    id: ListActionTypeV2.UNSUSPEND,
                    label: t('users.v2.okta.unsuspendUser'),
                    onClick: dispatch(ListActionTypeV2.UNSUSPEND),
                },
            ];
        case UserAccountStatus.EXPIRED:
        case UserAccountStatus.INACTIVE:
            return [
                {
                    id: ListActionTypeV2.ACTIVATE,
                    label: t('users.v2.okta.activateUser'),
                    onClick: dispatch(ListActionTypeV2.ACTIVATE),
                },
            ];
        default:
            return [];
    }
};
export const getRowMenuItems = (user, context) => {
    const dispatch = (action) => () => context.onRowAction({ action, data: user });
    const isSelf = user.id === getUserGuid();
    const isTsAdmin = user.id === getTSAdminUserGuid();
    const cannotDelete = isSelf || isTsAdmin;
    const cannotEdit = isTsAdmin && getUserGuid() !== getTSAdminUserGuid();
    const editItem = {
        id: ListActionTypeV2.EDIT,
        label: t(V2TranslationKeys.EDIT),
        onClick: dispatch(ListActionTypeV2.EDIT),
        itemDisabled: cannotEdit,
    };
    const resetPwdItem = {
        id: ListActionTypeV2.RESET_PASSWORD,
        label: t(V2TranslationKeys.RESET_PASSWORD),
        onClick: dispatch(ListActionTypeV2.RESET_PASSWORD),
    };
    const deleteItem = {
        id: ListActionTypeV2.DELETE,
        label: t(V2TranslationKeys.DELETE),
        onClick: dispatch(ListActionTypeV2.DELETE),
        itemDisabled: cannotDelete,
    };
    const exportItem = {
        id: ListActionTypeV2.EXPORT,
        label: t(V2TranslationKeys.EXPORT),
        onClick: dispatch(ListActionTypeV2.EXPORT),
    };
    const statusActions = getStatusSpecificActions(user, dispatch);
    const resetPwdItems = isLocalUser(user.type) ? [resetPwdItem] : [];
    const isActive = user.userState === UserAccountStatus.ACTIVE;
    const primaryGroup = {
        title: 'Primary',
        items: isActive ? [editItem] : [...statusActions, editItem],
    };
    const secondaryGroup = {
        title: 'Secondary',
        items: [
            ...resetPwdItems,
            ...(isActive ? statusActions : []),
            deleteItem,
        ],
    };
    const exportGroup = {
        title: 'Export',
        items: [exportItem],
    };
    const isTmlEnabled = isPrincipalImportExportEnabled() && isScriptabilityEnabled();
    const groups = [primaryGroup, secondaryGroup];
    if (isTmlEnabled) {
        groups.push(exportGroup);
    }
    return groups;
};
export const getBulkActions = (selectedUsers, handlers, isClusterAdmin) => {
    const isTmlEnabled = isPrincipalImportExportEnabled() && isScriptabilityEnabled();
    const exportAction = {
        id: 'export',
        label: t(V2TranslationKeys.EXPORT),
        onClick: handlers.onExport,
    };
    const deleteAction = {
        id: 'delete',
        label: t(V2TranslationKeys.DELETE),
        onClick: handlers.onDelete,
    };
    const addAction = isClusterAdmin
        ? {
            id: 'addToOrg',
            label: t(V2TranslationKeys.ADD_TO_ORG),
            onClick: handlers.onAddToOrg,
        }
        : {
            id: 'addToGroups',
            label: t(V2TranslationKeys.ADD_TO_GROUPS),
            onClick: handlers.onAddToGroups,
        };
    const tailActions = [
        addAction,
        ...(isTmlEnabled ? [exportAction] : []),
        deleteAction,
    ];
    const statuses = new Set(selectedUsers.map(u => u.userState));
    if (statuses.size === 1) {
        const status = [...statuses][0];
        switch (status) {
            case UserAccountStatus.LOCKED:
                return [
                    {
                        id: 'unlock',
                        label: t('oktaUser.UNLOCK'),
                        onClick: handlers.onUnlockAll,
                    },
                    ...tailActions,
                ];
            case UserAccountStatus.SUSPENDED:
                return [
                    {
                        id: 'unsuspend',
                        label: t('users.v2.okta.unsuspendUser'),
                        onClick: handlers.onUnsuspendAll,
                    },
                    ...tailActions,
                ];
            case UserAccountStatus.INACTIVE:
                return [
                    {
                        id: 'activate',
                        label: t('users.v2.okta.activateUser'),
                        onClick: handlers.onActivateAll,
                    },
                    ...tailActions,
                ];
            default:
                break;
        }
    }
    return tailActions;
};
const NameCell = ({ data, context }) => {
    const displayName = data.displayName || '';
    const userName = data.name || '';
    return (React.createElement(Vertical, { className: styles.usersNameCell },
        React.createElement(Typography, { variant: "p", color: Colors.accent, className: styles.usersNameLink, ellipsis: { rows: 1 }, tooltipConfig: {
                showTooltipOnlyOnEllipsis: true,
                dataTooltipContent: displayName,
            }, onClick: () => context.onRowAction({
                action: ListActionTypeV2.VIEW_DETAILS,
                data,
            }) }, displayName),
        React.createElement(Typography, { variant: "footnote", color: Colors.grayLight, ellipsis: { rows: 1 }, tooltipConfig: {
                showTooltipOnlyOnEllipsis: true,
                dataTooltipContent: userName,
            } }, userName)));
};
export const NamesListCell = ({ names }) => {
    const cellRef = useRef(null);
    const popoverRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(names.length);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    useLayoutEffect(() => {
        const el = cellRef.current;
        if (!el || names.length <= 1)
            return undefined;
        const measure = () => {
            const style = getComputedStyle(el);
            const padding = (parseFloat(style.paddingLeft) || 0) +
                (parseFloat(style.paddingRight) || 0);
            const cellWidth = el.clientWidth - padding;
            if (cellWidth <= 0)
                return;
            const ctx = document.createElement('canvas').getContext('2d');
            if (!ctx)
                return;
            ctx.font = style.font;
            let count = names.length;
            while (count > 1) {
                if (ctx.measureText(names.slice(0, count).join(' , ')).width <=
                    cellWidth)
                    break;
                count--;
            }
            setVisibleCount(count);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => {
            observer.disconnect();
        };
    }, [names]);
    if (names.length === 0) {
        return (React.createElement(Typography, { variant: "p", color: Colors.grayLight, className: styles.usersCellContent }, "-"));
    }
    const remaining = names.length - visibleCount;
    return (React.createElement("div", { ref: cellRef, className: styles.namesListCell },
        React.createElement(Typography, { variant: "p", ellipsis: { rows: 1 } }, names.slice(0, visibleCount).join(', ')),
        remaining > 0 && (React.createElement(React.Fragment, null,
            React.createElement("span", { ref: popoverRef, className: styles.moreLink, onClick: () => setIsPopoverOpen(!isPopoverOpen), onKeyDown: e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setIsPopoverOpen(!isPopoverOpen);
                    }
                }, role: "button", tabIndex: 0 }, t(V2TranslationKeys.N_MORE, { count: remaining })),
            React.createElement(Popover, { align: "bottom-start", targetRef: popoverRef, isVisible: isPopoverOpen, onClickOutside: () => setIsPopoverOpen(false), content: React.createElement(Vertical, { className: styles.popoverContent }, names.slice(visibleCount).map(name => (React.createElement(Typography, { key: name, variant: "body-normal", noMargin: true, className: styles.popoverItem }, name)))) })))));
};
const OrgsCell = ({ data, context }) => {
    const orgIds = data.orgIds || [];
    const orgNameMap = context?.orgNameMap || {};
    const names = orgIds.map(id => orgNameMap[id]).filter(Boolean);
    return React.createElement(NamesListCell, { names: names });
};
const GroupsCell = () => React.createElement(NamesListCell, { names: [] });
const AuthTypeCell = ({ data }) => (React.createElement(Typography, { variant: "p", className: styles.usersCellContent }, getAuthTypeLabel(data.type)));
const CreatedCell = ({ data }) => (React.createElement(Typography, { variant: "p", className: styles.usersCellContent }, getRelativeTime(data.created)));
const StatusCell = ({ data }) => {
    const status = data.userState;
    const label = getStatusDisplayLabel(status);
    return (React.createElement(Typography, { variant: "p", color: Colors.base, className: styles.usersCellContent }, label));
};
const ActionsCell = ({ data, context }) => {
    if (data.readOnly)
        return null;
    const menuItemGroups = getRowMenuItems(data, context);
    return (React.createElement(MoreOptions, { menuId: 1, menuLabel: t(V2TranslationKeys.COL_MORE_OPTIONS), menuItemGroups: menuItemGroups, isDisabled: data.isChecked }));
};
export const getColumnDefinitionsV2 = (isClusterAdmin) => [
    {
        cellRendererFramework: NameCell,
        headerName: t(V2TranslationKeys.COL_NAME),
        headerClass: [styles.headerStyle],
        colId: TableColumnId.NAME,
        suppressMovable: true,
        sortable: true,
        initialSort: 'asc',
        flex: 2,
    },
    {
        cellRendererFramework: isClusterAdmin ? OrgsCell : GroupsCell,
        headerName: isClusterAdmin
            ? t(V2TranslationKeys.COL_ORGS)
            : t(V2TranslationKeys.COL_GROUPS),
        headerClass: [styles.headerStyle],
        colId: TableColumnId.GROUPS_OR_ORGS,
        suppressMovable: true,
        sortable: false,
        flex: 2,
    },
    {
        cellRendererFramework: AuthTypeCell,
        headerName: t(V2TranslationKeys.COL_AUTH_TYPE),
        headerClass: [styles.headerStyle],
        colId: TableColumnId.AUTH_TYPE,
        suppressMovable: true,
        sortable: false,
        flex: 1,
    },
    {
        cellRendererFramework: CreatedCell,
        headerName: t(V2TranslationKeys.COL_CREATED),
        headerClass: [styles.headerStyle],
        colId: TableColumnId.CREATED,
        suppressMovable: true,
        sortable: true,
        flex: 1,
    },
    {
        cellRendererFramework: StatusCell,
        headerName: t(V2TranslationKeys.COL_STATUS),
        headerClass: [styles.headerStyle],
        colId: TableColumnId.STATUS,
        suppressMovable: true,
        sortable: false,
        flex: 1,
    },
    {
        cellRendererFramework: ActionsCell,
        colId: TableColumnId.ACTIONS,
        headerName: t(V2TranslationKeys.COL_ACTIONS),
        headerClass: [styles.headerStyle],
        cellClass: [styles.usersActionsCell],
        sortable: false,
        width: 78,
        suppressMovable: true,
    },
];
export const cleanUserPayload = (obj) => {
    const cleaned = deepOmit(obj, ['__typename', 'displayNameLastUpdatedBy']);
    if (!cleaned.userContent?.userPreferences?.preferredLocale) {
        delete cleaned.userContent?.userPreferences?.preferredLocale;
    }
    if (!cleaned.userContent?.userProperties?.persona) {
        delete cleaned.userContent?.userProperties?.persona;
    }
    return cleaned;
};
export const handleGraphQLError = (err, onError, translate) => {
    const graphQlError = getErrorObjectFromGraphQLErrors(err);
    const errorCode = getErrorCode(graphQlError);
    const info = getTranslatedInfoForMessageCode(errorCode);
    onError(translate(info.summary));
    return { graphQlError, errorCode, info };
};
//# sourceMappingURL=utils.js.map